import React, { ChangeEvent, FC, useCallback, useState, useMemo, useEffect, memo } from 'react';
import styled from 'styled-components';
import { useFormikContext } from 'formik';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _some from 'lodash/some';
import _includes from 'lodash/includes';
import { gqlTalentUnionEnum as TALENT_UNION } from '@minecraft.graphql-types';
import UncontrolledRadioGroup from '@minecraft.uncontrolled-radio-group';
import Checkbox from '@minecraft.checkbox';
import { useDrawerButtons, useDrawerSearchFilterNameOrCategory } from '@minecraft.drawer';
import { useUnionStatusOptions } from '@blocs.filter';
import { useTranslation } from '@blocs.i18n';
import { TALENT_UNION_NON_MEMBER, sagAftraChildrenExclusion } from '../../../shared/config';
import { FiltersNameEnum, FilterData, UnionStatusIndicatorEnum, SelectOption } from '../../../shared/types';
import { shouldNonUnionBeChecked } from '../../../shared/utils';
import { Row, Column, Label } from '../../../shared/styles';
import { FiltersCategoryNameEnum } from '../../shared/types';
import { sagAftraChildren } from '../../shared/config';

const CheckboxGroup = styled.div`
  margin-left: 1.25rem;
`;

const StyledColumn = styled(Column)`
  padding-bottom: 2rem;
`;

const StyledCheckbox = styled(Checkbox)`
  margin: 0;

  &:not(:first-child) {
    margin-top: 0.75rem;
  }
`;

const NonUnionCheckbox = styled(Checkbox)`
  margin: 0.5rem 0 1.5rem;
`;

type Props = Pick<FilterData, 'label'>;

const getRadioGroupDefaultValue = ({
  showSagAftraChildren,
  currentUnionStatus,
  unionStatus,
  excludeUnionStatus,
}): UnionStatusIndicatorEnum => {
  if (currentUnionStatus === TALENT_UNION.SAG_AFTRA && showSagAftraChildren) {
    return UnionStatusIndicatorEnum.specifyStatus;
  }

  if (_find(unionStatus, (item) => item.value === currentUnionStatus)) {
    return UnionStatusIndicatorEnum.member;
  }

  if (_find(excludeUnionStatus, (item) => item.value === currentUnionStatus)) {
    return UnionStatusIndicatorEnum.nonMember;
  }

  return UnionStatusIndicatorEnum.noPreference;
};

const shouldCheckboxBeChecked = ({ isSagAftraNonMemberCheckboxChecked, currentUnionStatus, unionStatus }): boolean => {
  if (currentUnionStatus === TALENT_UNION_NON_MEMBER) {
    return isSagAftraNonMemberCheckboxChecked;
  }

  return Boolean(_find(unionStatus, (item) => item.value === currentUnionStatus));
};

const removeUnionStatus = (unionStatuses: SelectOption[], currentUnionStatuses: TALENT_UNION[]): SelectOption[] => {
  return _filter(unionStatuses, (unionStatus) => !_includes(currentUnionStatuses, unionStatus.value));
};

const UnionStatus: FC<Props> = ({ label }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const unionStatusOptions = useUnionStatusOptions();

  const getUnionStatusOption = (unionStatusValue: TALENT_UNION): SelectOption => {
    return _find(unionStatusOptions, { value: unionStatusValue });
  };

  const addUnionStatus = (unionStatuses: SelectOption[], currentUnionStatuses: TALENT_UNION[]): SelectOption[] => {
    const isSomeSagAftraChildrenExclusionChecked = _some(sagAftraChildrenExclusion, (sagAftraChild) =>
      _includes(currentUnionStatuses, sagAftraChild)
    );
    const unionStatusesWithoutNonUnion = _filter(unionStatuses, (option) => option.value !== TALENT_UNION.NON_UNION);

    return [
      ...(isSomeSagAftraChildrenExclusionChecked ? unionStatuses : unionStatusesWithoutNonUnion),
      ..._map(currentUnionStatuses, (currentUnionStatus) => getUnionStatusOption(currentUnionStatus)),
    ];
  };

  const { isCancelButtonClicked: isClearAllButtonClicked } = useDrawerButtons();
  const { getMatchedSearchOptions } = useDrawerSearchFilterNameOrCategory(FiltersCategoryNameEnum.unionStatus);

  const isUnionStatusContainsAnySagAftraChildren = useMemo(
    () => _some(values[FiltersNameEnum.unionStatus], (item) => _includes(sagAftraChildren, item.value)),
    [values[FiltersNameEnum.unionStatus]]
  );

  const isExcludeUnionStatusContainsAnySagAftraChildren = useMemo(
    () => _some(values[FiltersNameEnum.excludeUnionStatus], (item) => _includes(sagAftraChildren, item.value)),
    [values[FiltersNameEnum.excludeUnionStatus]]
  );

  const [isSagAftraNonMemberCheckboxChecked, setSagAftraNonMemberCheckboxChecked] = useState(
    isExcludeUnionStatusContainsAnySagAftraChildren
  );
  const [showSagAftraChildren, setSagAftraChildrenVisibility] = useState(
    isExcludeUnionStatusContainsAnySagAftraChildren || isUnionStatusContainsAnySagAftraChildren
  );

  const setNonSagAftraRadioButton = useCallback(
    (currentUnionStatus, currentUnionStatusIndicator) => {
      switch (currentUnionStatusIndicator) {
        case UnionStatusIndicatorEnum.noPreference:
          setFieldValue(
            FiltersNameEnum.excludeUnionStatus,
            removeUnionStatus(values[FiltersNameEnum.excludeUnionStatus], [currentUnionStatus]),
            false
          );
          setFieldValue(
            FiltersNameEnum.unionStatus,
            removeUnionStatus(values[FiltersNameEnum.unionStatus], [currentUnionStatus]),
            false
          );
          break;
        case UnionStatusIndicatorEnum.member:
          setFieldValue(
            FiltersNameEnum.unionStatus,
            addUnionStatus(values[FiltersNameEnum.unionStatus], [currentUnionStatus]),
            false
          );
          setFieldValue(
            FiltersNameEnum.excludeUnionStatus,
            removeUnionStatus(values[FiltersNameEnum.excludeUnionStatus], [currentUnionStatus]),
            false
          );
          break;
        case UnionStatusIndicatorEnum.nonMember:
          setFieldValue(
            FiltersNameEnum.excludeUnionStatus,
            addUnionStatus(values[FiltersNameEnum.excludeUnionStatus], [currentUnionStatus]),
            false
          );
          setFieldValue(
            FiltersNameEnum.unionStatus,
            removeUnionStatus(values[FiltersNameEnum.unionStatus], [currentUnionStatus]),
            false
          );
          break;
        default:
          break;
      }
    },
    [values[FiltersNameEnum.unionStatus], values[FiltersNameEnum.excludeUnionStatus]]
  );

  const setSagAftraRadioButton = useCallback(
    (currentUnionStatusIndicator) => {
      switch (currentUnionStatusIndicator) {
        case UnionStatusIndicatorEnum.noPreference:
          setFieldValue(
            FiltersNameEnum.excludeUnionStatus,
            removeUnionStatus(values[FiltersNameEnum.excludeUnionStatus], sagAftraChildren),
            false
          );
          setFieldValue(
            FiltersNameEnum.unionStatus,
            removeUnionStatus(values[FiltersNameEnum.unionStatus], [...sagAftraChildren, TALENT_UNION.NON_UNION]),
            false
          );
          setSagAftraChildrenVisibility(false);
          setSagAftraNonMemberCheckboxChecked(false);
          break;
        case UnionStatusIndicatorEnum.specifyStatus:
          setSagAftraChildrenVisibility(true);
          break;
        default:
          break;
      }
    },
    [values[FiltersNameEnum.unionStatus], values[FiltersNameEnum.excludeUnionStatus]]
  );

  const setSagAftraChildrenCheckbox = useCallback(
    (currentSagAftraUnionStatus: TALENT_UNION): void => {
      const isCurrentSagAftraUnionStatusExist = Boolean(
        _find(values[FiltersNameEnum.unionStatus], { value: currentSagAftraUnionStatus })
      );

      if (isCurrentSagAftraUnionStatusExist) {
        setFieldValue(
          FiltersNameEnum.unionStatus,
          removeUnionStatus(values[FiltersNameEnum.unionStatus], [currentSagAftraUnionStatus]),
          false
        );

        if (isSagAftraNonMemberCheckboxChecked) {
          setFieldValue(
            FiltersNameEnum.excludeUnionStatus,
            addUnionStatus(values[FiltersNameEnum.excludeUnionStatus], [currentSagAftraUnionStatus]),
            false
          );
        }
      } else {
        setFieldValue(
          FiltersNameEnum.unionStatus,
          addUnionStatus(values[FiltersNameEnum.unionStatus], [currentSagAftraUnionStatus]),
          false
        );
        setFieldValue(
          FiltersNameEnum.excludeUnionStatus,
          removeUnionStatus(values[FiltersNameEnum.excludeUnionStatus], [currentSagAftraUnionStatus]),
          false
        );
      }
    },
    [
      isSagAftraNonMemberCheckboxChecked,
      values[FiltersNameEnum.unionStatus],
      values[FiltersNameEnum.excludeUnionStatus],
    ]
  );

  const setSagAftraNonMemberCheckbox = useCallback(
    (isСurrentSagAftraUnionStatusChecked: boolean): void => {
      const uncheckedSagAftraChildren = _filter(
        sagAftraChildren,
        (item) => !_find(values[FiltersNameEnum.unionStatus], { value: item })
      );

      if (isСurrentSagAftraUnionStatusChecked) {
        setFieldValue(
          FiltersNameEnum.excludeUnionStatus,
          addUnionStatus(values[FiltersNameEnum.excludeUnionStatus], uncheckedSagAftraChildren),
          false
        );
        setSagAftraNonMemberCheckboxChecked(true);
      } else {
        setFieldValue(
          FiltersNameEnum.excludeUnionStatus,
          removeUnionStatus(values[FiltersNameEnum.excludeUnionStatus], uncheckedSagAftraChildren),
          false
        );
        setFieldValue(
          FiltersNameEnum.unionStatus,
          removeUnionStatus(values[FiltersNameEnum.unionStatus], [TALENT_UNION.NON_UNION]),
          false
        );
        setSagAftraNonMemberCheckboxChecked(false);
      }
    },
    [values[FiltersNameEnum.unionStatus], values[FiltersNameEnum.excludeUnionStatus]]
  );

  const handleRadioButtonChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, value: string): void => {
      const currentUnionStatus = event.currentTarget.name;
      const currentUnionStatusIndicator = value;

      if (currentUnionStatus === TALENT_UNION.SAG_AFTRA) {
        setSagAftraRadioButton(currentUnionStatusIndicator);
      } else {
        setNonSagAftraRadioButton(currentUnionStatus, currentUnionStatusIndicator);
      }
    },
    [setNonSagAftraRadioButton, setSagAftraRadioButton]
  );

  const handleCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const isСurrentSagAftraUnionStatusChecked = event.currentTarget.checked;
      const currentSagAftraUnionStatus = event.currentTarget.value as TALENT_UNION & typeof TALENT_UNION_NON_MEMBER;

      if (currentSagAftraUnionStatus === TALENT_UNION_NON_MEMBER) {
        setSagAftraNonMemberCheckbox(isСurrentSagAftraUnionStatusChecked);
      } else {
        setSagAftraChildrenCheckbox(currentSagAftraUnionStatus);
      }
    },
    [setSagAftraChildrenCheckbox, setSagAftraNonMemberCheckbox]
  );

  const handleNonUnionCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const isNonUnionChecked = event.currentTarget.checked;
      const isSomeSagAftraChildrenExclusionChecked = _some(values[FiltersNameEnum.unionStatus], (option) =>
        _includes(sagAftraChildrenExclusion, option.value)
      );
      const nonUnionStatus = _find(unionStatusOptions, { value: TALENT_UNION.NON_UNION });
      const unionStatusOptionsWithoutNonUnionStatus = _filter(
        unionStatusOptions,
        (item) => item.value !== TALENT_UNION.NON_UNION
      );

      if (isNonUnionChecked && isSomeSagAftraChildrenExclusionChecked) {
        const checkedSagAftraChildrenExclusion = _filter(values[FiltersNameEnum.unionStatus], (option) =>
          _includes(sagAftraChildrenExclusion, option.value)
        );
        const checkedSagAftraChildrenExclusionValues = _map(checkedSagAftraChildrenExclusion, (option) => option.value);

        setFieldValue(FiltersNameEnum.unionStatus, [nonUnionStatus, ...checkedSagAftraChildrenExclusion], false);
        setFieldValue(
          FiltersNameEnum.excludeUnionStatus,
          removeUnionStatus(unionStatusOptionsWithoutNonUnionStatus, checkedSagAftraChildrenExclusionValues),
          false
        );
        setSagAftraNonMemberCheckboxChecked(true);
      } else if (isNonUnionChecked) {
        setFieldValue(FiltersNameEnum.excludeUnionStatus, unionStatusOptionsWithoutNonUnionStatus, false);
        setFieldValue(FiltersNameEnum.unionStatus, [nonUnionStatus], false);
        setSagAftraChildrenVisibility(true);
        setSagAftraNonMemberCheckboxChecked(true);
      } else {
        setFieldValue(FiltersNameEnum.excludeUnionStatus, [], false);
        setFieldValue(
          FiltersNameEnum.unionStatus,
          removeUnionStatus(values[FiltersNameEnum.unionStatus], [TALENT_UNION.NON_UNION]),
          false
        );
        setSagAftraChildrenVisibility(isSomeSagAftraChildrenExclusionChecked);
        setSagAftraNonMemberCheckboxChecked(false);
      }
    },
    [values[FiltersNameEnum.unionStatus]]
  );

  // Update state when autofill value is changed
  useEffect(() => {
    setSagAftraNonMemberCheckboxChecked(isExcludeUnionStatusContainsAnySagAftraChildren);
  }, [values[FiltersNameEnum.autofillByRoleCriteria], isClearAllButtonClicked]);

  useEffect(() => {
    setSagAftraChildrenVisibility(
      isExcludeUnionStatusContainsAnySagAftraChildren || isUnionStatusContainsAnySagAftraChildren
    );
  }, [values[FiltersNameEnum.autofillByRoleCriteria], isClearAllButtonClicked]);

  const unionStatusGroupOptions = getMatchedSearchOptions(FiltersNameEnum.unionStatus);

  return (
    <>
      <Label>{label}</Label>
      <NonUnionCheckbox
        isChecked={shouldNonUnionBeChecked(values[FiltersNameEnum.unionStatus])}
        value={TALENT_UNION.NON_UNION}
        label={t('common:profile.nonUnion')}
        onChange={handleNonUnionCheckboxChange}
        data-qa-id="unionStatus-nonUnion"
      />
      <Row>
        {_map(unionStatusGroupOptions, (unionStatusItem) => (
          <StyledColumn key={unionStatusItem.label} data-qa-id={unionStatusItem.name}>
            <Label>{unionStatusItem.label}</Label>
            <UncontrolledRadioGroup
              enableReinitializeDefaultValue
              name={unionStatusItem.name}
              options={unionStatusItem.radioGroup}
              defaultValue={getRadioGroupDefaultValue({
                showSagAftraChildren,
                currentUnionStatus: unionStatusItem.name,
                unionStatus: values[FiltersNameEnum.unionStatus],
                excludeUnionStatus: values[FiltersNameEnum.excludeUnionStatus],
              })}
              onChange={handleRadioButtonChange}
              data-qa-id="unionStatus"
            />
            {showSagAftraChildren && (
              <CheckboxGroup>
                {_map(unionStatusItem.checkboxGroup, (checkbox) => (
                  <StyledCheckbox
                    key={checkbox.value}
                    isChecked={shouldCheckboxBeChecked({
                      isSagAftraNonMemberCheckboxChecked,
                      currentUnionStatus: checkbox.value,
                      unionStatus: values[FiltersNameEnum.unionStatus],
                    })}
                    value={checkbox.value}
                    label={checkbox.label}
                    onChange={handleCheckboxChange}
                  />
                ))}
              </CheckboxGroup>
            )}
          </StyledColumn>
        ))}
      </Row>
    </>
  );
};

export default memo(UnionStatus);
